"use client";

import { useI18n } from "@wojo/localization";
import { Button, Card, Table, Text } from "@wojo/ui";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { GroupMemberProspectDetailsFragment } from "generated/graphql";
import React, { useId } from "react";
import styles from "../base-table/BaseTable.module.scss";
import { EmailTravelersButton } from "../email-travelers-button/EmailTravelersButton";
import { TravelerContactInfo } from "../traveler-contact-info/TravelerContactInfo";
import { TravelerNameCheckbox } from "../traveler-name-checkbox/TravelerNameCheckbox";
import { useEmailSelection } from "../../_client/hooks/use-email-selection";

dayjs.extend(utc);

export type InterestedTravelersTableProps = {
    interestedTravelers: GroupMemberProspectDetailsFragment[];
    openAddInterestedTravelerModal: () => void;
};

export const InterestedTravelersTable: React.FC<
    InterestedTravelersTableProps
> = ({ interestedTravelers, openAddInterestedTravelerModal }) => {
    const emailHelpTextId = useId();
    const i18n = useI18n("account").account.myGroupMembers;
    const { selectedEmailsMap, allEmails, handleCheckboxChange } =
        useEmailSelection({
            prospectiveTravelers: interestedTravelers,
        });
    const selectedEmails = Array.from(selectedEmailsMap.values()).filter(
        (email) => email,
    );

    return (
        <>
            <Card>
                <div className={styles.table}>
                    <Table
                        aria-describedby={emailHelpTextId}
                        header={
                            <Table.Header>
                                <Table.Cell>
                                    {i18n.interestedTravelersTable.nameHeader.l()}
                                </Table.Cell>
                                <Table.Cell
                                    className={styles["offset-checkbox"]}
                                >
                                    {i18n.interestedTravelersTable.dateAddedHeader.l()}
                                </Table.Cell>
                                <Table.Cell
                                    className={styles["offset-checkbox"]}
                                >
                                    {i18n.interestedTravelersTable.interestLevelHeader.l()}
                                </Table.Cell>
                                <Table.Cell
                                    className={styles["offset-checkbox"]}
                                >
                                    {i18n.interestedTravelersTable.contactInfoHeader.l()}
                                </Table.Cell>
                            </Table.Header>
                        }
                        showTitle={false}
                        tableTitle={
                            <Text.Heading
                                order="3"
                                style={{ paddingBottom: "var(--g-spacing-md)" }}
                            >
                                {i18n.interestedTravelersTable.tableTitle.l()}
                            </Text.Heading>
                        }
                    >
                        {interestedTravelers.map((prospect) => {
                            const address = prospect.address ?? undefined;
                            const email = prospect.email ?? "";
                            const phoneNumber = prospect.phoneNumber ?? "";
                            return (
                                <Table.Row
                                    key={prospect._id}
                                    header={
                                        <Table.Cell>
                                            <TravelerNameCheckbox
                                                id={prospect._id}
                                                email={email}
                                                selectedEmailsMap={
                                                    selectedEmailsMap
                                                }
                                                handleEmailCheckboxChange={
                                                    handleCheckboxChange
                                                }
                                                firstName={prospect.firstName}
                                                lastName={
                                                    prospect.lastName ?? ""
                                                }
                                            />
                                        </Table.Cell>
                                    }
                                >
                                    <Table.Cell>
                                        <Text.Body variant="2">
                                            {dayjs
                                                .utc(prospect.createdAt)
                                                .format("MMM D, YYYY")}
                                        </Text.Body>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Text.Body
                                            variant="2"
                                            style={{
                                                textTransform: "capitalize",
                                            }}
                                        >
                                            {prospect.levelOfInterest
                                                ? prospect.levelOfInterest.toLowerCase()
                                                : "—"}
                                        </Text.Body>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <TravelerContactInfo
                                            address={address}
                                            phoneNumber={phoneNumber}
                                            email={email}
                                        />
                                    </Table.Cell>
                                </Table.Row>
                            );
                        })}
                    </Table>
                </div>
                <div className={styles.footer}>
                    <EmailTravelersButton
                        emails={selectedEmails}
                        allEmails={allEmails}
                        buttonText={
                            selectedEmails.length === 0
                                ? i18n.interestedTravelers.emailAllInterestedTravelers.l()
                                : i18n.interestedTravelers.emailSelectedInterestedTravelers.l()
                        }
                    />
                    <Button
                        onClick={openAddInterestedTravelerModal}
                        variant="secondary"
                        type="button"
                    >
                        {i18n.interestedTravelers.addInterestedTraveler.l()}
                    </Button>
                </div>
            </Card>
            <section>
                <Text.Body
                    id={emailHelpTextId}
                    variant={"2"}
                    style={{
                        paddingTop: "var(--g-spacing-sm)",
                        color: "var(--s-color-text-quiet)",
                    }}
                >
                    {i18n.bookedTravelers.emailHelpText.l()}
                </Text.Body>
            </section>
        </>
    );
};
